// MapView for web/browser

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import Constants from 'expo-constants';
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import { DefaultRenderer, MarkerClusterer, SuperClusterViewportAlgorithm } from "@googlemaps/markerclusterer";
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from "@/utilities/AppUtils";
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import CustomActivityIndicator from '@/components/CustomActivityIndicator';
import { AppContext } from '@/components/AppContext';
import { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';

const RED_CIRCLE_ICON = {
  url: "data:image/svg+xml;base64," + global.btoa(`
    <svg fill="${AppConsts.COLOR6}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
      <circle cx="120" cy="120" r="70" />    
    </svg>
  `),
};

const ICON_TEXT_COLOR = "#ffffff";
const ICON_TEXT_FONT_SIZE = "12px";
const ICON_TEXT_FONT_WEIGHT = "bold";

// Might be called multiple times so check the status
// This is used because otherwise APIs like google.maps aren't loaded yet in the global scope
function googleMapsLoaderCallback(status, loader) {
  if (status == "SUCCESS") {
    if (!RED_CIRCLE_ICON.scaledSize) {
      RED_CIRCLE_ICON.scaledSize = new google.maps.Size(50, 50);
    }
  }
}

class CircleRendererCustomized2 {
  render({ count, position }, stats) {
    return new google.maps.Marker({
      position,
      icon: RED_CIRCLE_ICON,
      label: {
        text: String(count),
        color: ICON_TEXT_COLOR,
        fontSize: ICON_TEXT_FONT_SIZE,
        fontWeight: ICON_TEXT_FONT_WEIGHT,
      },
      // markers with a larger count should be on top
      zIndex: google.maps.Marker.MAX_ZINDEX + count,

      // https://developers.google.com/maps/documentation/javascript/markers#optimize
      // Unclear exactly how this works, no perceivable performance difference, and the
      // docs state that the API will try to optimize when possible
      //optimized: true,
    });
  }
}

// We just want to show things at a pretty coarse level
const MAX_MAP_ZOOM = AppConstants.ZOOM_LEVELS.CITY + 3;

// We don't want to de-cluster below the ~city level
const MAX_CLUSTERER_ZOOM = MAX_MAP_ZOOM + 1;

function MyMapComponent({ desiredGenders, ageMin, ageMax, heightMin, heightMax }) {
  const mapDiv = useRef(null);
  const map = useRef(null);
  const infoWindowPopup = useRef(null);
  const clusterer = useRef(null);
  const [locations, setLocations] = useState([]);
  const [mapFullyLoaded, setMapFullyLoaded] = useState(false);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const domain = useSelector((state) => state.user.domain);
  const cornerInfoWrapper = useRef(null);
  const cornerInfoSpan = useRef(null);

  // Once the div is ready, initialize the map
  useEffect(() => {
    if (mapDiv.current) {

      const rightIconsPosition = google.maps.ControlPosition.RIGHT_BOTTOM;

      // https://developers.google.com/maps/documentation/javascript/reference/map
      // https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions

      const mapOptions = {
        center: AppConstants.CENTER_UNITED_STATES,
        zoom: 3,
        styles: AppConstants.MAP_FEATURES,
        streetViewControl: false,
        mapTypeControl: false, // Hide top left Map/Satellite buttons
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: {
          position: rightIconsPosition,
        },
        gestureHandling: 'greedy',

        // This is the maximum zoom level of the map
        maxZoom: MAX_MAP_ZOOM,

        // TODO: clusters disappear above this zoom level
        minZoom: 3,
      };

      map.current = new google.maps.Map(mapDiv.current, mapOptions);

      cornerInfoWrapper.current = document.createElement("div");
      cornerInfoWrapper.current.style = "background-color: " + AppConsts.MAIN_COLOR1 + "; border: 0; border-radius: 2px; box-shadow: 0 1px 4px -1px rgba(0, 0, 0, 0.3); margin: 10px; padding: 8px; overflow: hidden;";
      cornerInfoSpan.current = document.createElement("span");
      cornerInfoSpan.current.textContent = "";
      cornerInfoSpan.current.style = "";
      cornerInfoWrapper.current.appendChild(cornerInfoSpan.current);
      map.current.controls[google.maps.ControlPosition.LEFT_BOTTOM].push(cornerInfoWrapper.current);

      // https://developers.google.com/maps/documentation/javascript/reference/info-window
      infoWindowPopup.current = new google.maps.InfoWindow({
        content: "",
        //disableAutoPan: true,
      });
      map.current.addListener("click", closeInfoPopup);

      google.maps.event.addListenerOnce(map.current, 'tilesloaded', onTilesLoaded);
    }
  }, [mapDiv]);

  useEffect(() => {
    if (mapFullyLoaded) {
      AppUtilities.info("MapView.useEffect: received new filter: desiredGenders: " + desiredGenders + ", ageMin: " + ageMin + ", ageMax: " + ageMax + ", heightMin: " + heightMin + ", heightMax: " + heightMax);
      updateMapLocations();
    }
  }, [mapFullyLoaded, desiredGenders, ageMin, ageMax, heightMin, heightMax]);

  function closeInfoPopup() {
    if (infoWindowPopup.current) {
      try {
        infoWindowPopup.current.close();
      } catch (e) {
        AppUtilities.info("MapView.closeInfoPopup: Error closing popup: " + AppUtilities.getErrors(e));
      }
    } else {
      AppUtilities.info("MapView.closeInfoPopup: Error closing popup: " + AppUtilities.getErrors(e));
    }
  }

  async function onTilesLoaded() {
    AppUtilities.info("MapView.onTilesLoaded map finished loading tiles");
    setMapFullyLoaded(true);
  }

  // Once the tiles are loaded, then we can load the actual locations
  async function updateMapLocations() {
    try {
      AppUtilities.info("MapView.updateMapLocations fetching data");

      showSpinner();

      const response = await AppUtilities.fetchUnauthorized(
        "MapView.updateMapLocations",
        "/rabbl/api/search_public",
        "POST",
        {
          domain: domain,
          desiredGenders: desiredGenders,
          desired_age_min: ageMin,
          desired_age_max: ageMax,
          desired_height_min: heightMin,
          desired_height_max: heightMax,
        },
        undefined,
        undefined,
        undefined,
        // The pins could be a lot of data, so we don't want to log it
        true
      );

      setLocations(response.body.pins);

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => updateMapLocations(), true);
      AppUtilities.handleException(e, "MapView.updateMapLocations");
    } finally {
      hideSpinner();
    }
  }

  useEffect(() => {
    if (AppUtilities.DEBUGLEVEL.map) {
      AppUtilities.info("MapView.updateMapLocations update locations: updating");
    }

    if (map.current && locations) {

      const actualMarkers = [];

      if (AppUtilities.DEBUGLEVEL.map) {
        AppUtilities.info("MapView.updateMapLocations update locations: updating with " + locations.length + " locations");
      }

      for (const location of locations) {
        const actualMarker = new google.maps.Marker({
          position: {
            lat: location.l,
            lng: location.g,
          },
          //icon: icons[mapPinOffsets[marker.p ? AppConstants.MAP_PIN_TYPES[marker.p] : AppConstants.MAP_PIN_NAMES.GENERIC]].uri,
          //title: location.t,
          title: "",
        });

        /*actualMarker.addListener("click", () => {

          infoWindowPopup.current.setContent(`
            <div
              onclick="document.dispatchEvent(new CustomEvent('map-info-click', { 'detail': '${location.t}' })); return false;"
              class="mapInfoPopup"
            >
              <h3 style="text-align: center;">${AppUtilities.escapeHTML(location.t)}</h3>
            </div>
          `);

          // https://developers.google.com/maps/documentation/javascript/reference/info-window#InfoWindow.open
          // https://developers.google.com/maps/documentation/javascript/reference/info-window#InfoWindowOpenOptions
          infoWindowPopup.current.open({
            map: map.current,
            anchor: actualMarker,
            // Otherwise the close button gets focus and a blue border
            shouldFocus: false,
          });
          map.current.panTo(actualMarker.getPosition());
        });*/

        actualMarkers.push(actualMarker);
      }

      if (AppUtilities.DEBUGLEVEL.map) {
        AppUtilities.info("MapView.updateMapLocations update locations: actualMarkers = " + actualMarkers.length);
      }

      if (actualMarkers.length > 0) {
        // https://googlemaps.github.io/js-markerclusterer/interfaces/MarkerClustererOptions.html
        if (!clusterer.current) {
          let newClusterer = new MarkerClusterer({
            map: map.current,
            markers: actualMarkers,
            //renderer: new DefaultRenderer(),
            renderer: new CircleRendererCustomized2(),
            algorithm: new SuperClusterViewportAlgorithm({

              // This is the zoom level at which we stop clustering
              maxZoom: MAX_CLUSTERER_ZOOM,

              viewportPadding: 60,

              // Not valid
              //minPoints: 1,
            }),
          });

          google.maps.event.addListener(newClusterer, 'click', function(cluster) {
            // If the info window is open, we have to close it first before
            // the clusterer can zoom in
            closeInfoPopup();
          });

          clusterer.current = newClusterer;
        } else {
          clusterer.current.clearMarkers();
          clusterer.current.addMarkers(actualMarkers);
        }
      } else {
        if (clusterer.current) {
          clusterer.current.clearMarkers();
        }
      }

      cornerInfoSpan.current.textContent = "Total: " + actualMarkers.length;
    }
  }, [locations]);

  useEffect(() => {
    if (locations && locations.length > 0) {

      const mapInfoClick = (e) => {
      };

      const keydownHandler = (e) => {
        if(e.keyCode == 27) {
          // ESC pressed
          closeInfoPopup();
        }
      };

      document.addEventListener("map-info-click", mapInfoClick);
      document.addEventListener("keydown", keydownHandler);

      return () => {
        document.removeEventListener("map-info-click", mapInfoClick);
        document.removeEventListener("keydown", keydownHandler);
      };
    }
  }, [locations]);

  return (
    <>
      <div ref={mapDiv} style={StyleSheet.absoluteFill} />
    </>
  );
}

export function MapView({ desiredGenders, ageMin, ageMax, heightMin, heightMax }) {
  const renderNonMap = (status) => {
    if (status == Status.LOADING) {
      return <CustomActivityIndicator />;
    }

    // Return null on Status.SUCCESS and no need to handle Status.FAILURE as Google Maps will render:
    //   Oops! Somethin went wrong.
    //   This page didn't load Google Maps correctly. See the JavaScript console for technical details.
    return null;
  };

  return (
    <Wrapper apiKey={Constants.expoConfig.extra.googleWebApiKey} render={renderNonMap} callback={googleMapsLoaderCallback}>
      <MyMapComponent desiredGenders={desiredGenders} ageMin={ageMin} ageMax={ageMax} heightMin={heightMin} heightMax={heightMax} />
    </Wrapper>
  );
}
